import { PIX_CODE_FORMATS } from '../../../flowConstants';
import FlowPaySwitch from '../FlowPaySwitch';
import AutoGrowTextarea from '../AutoGrowTextarea';

const INPUT_CLS = `flex w-full rounded-xl border px-3 py-1.5 placeholder:text-white/20 transition-all hover:border-white/10 focus:outline-none focus:ring-1 focus:ring-[var(--brand-500)]/30 focus:border-[var(--brand-500)]/30 h-7 bg-[#111111] text-white text-xs mt-1`;
const TEXTAREA_CLS = `flex w-full rounded-xl border border-dashed border-white/[0.06] px-3 py-2 placeholder:text-white/20 transition-all hover:border-white/15 outline-none focus:border-[var(--brand-500)]/40 min-h-[50px] bg-[#111111] text-white text-xs mt-1 resize-none`;
const SELECT_CLS = `flex w-full rounded-xl border border-white/[0.06] px-3 py-1.5 h-7 bg-[#111111] text-white text-xs mt-1 focus:outline-none focus:ring-1 focus:ring-[var(--brand-500)]/30`;

const PAYMENT_BUTTONS = [
    { key: 'verify', checkedKey: 'showVerifyButton', textKey: 'verifyButtonText', defaultText: '✅Verificar Pagamento', placeholder: 'Ex: ✅ CONFIRMAR PAGAMENTO' },
    { key: 'copy',   checkedKey: 'showCopyButton',   textKey: 'copyButtonText',   defaultText: '📋 Copiar Código',      placeholder: 'Ex: 📋 COPIAR CÓDIGO' },
    { key: 'qr',     checkedKey: 'showQrButton',     textKey: 'qrButtonText',     defaultText: '📱Ver QR Code',         placeholder: 'Ex: 📱 VER QR CODE' },
];

export default function PaymentNode({ id, data }) {
    const paymentPlanName       = data?.paymentPlanName || '';
    const paymentPlanDuration   = data?.paymentPlanDuration || 'vitalicio';
    const paymentAmount         = data?.paymentAmount ?? '';
    const pixCodeFormat         = data?.pixCodeFormat || 'blockquote';
    const messageBefore         = data?.messageBefore || '';
    const messageAfter          = data?.messageAfter || '';
    const showQrButton          = data?.showQrButton !== false;
    const showCopyButton        = data?.showCopyButton !== false;
    const showVerifyButton      = data?.showVerifyButton !== false;
    const verifyButtonText      = data?.verifyButtonText || '✅Verificar Pagamento';
    const copyButtonText        = data?.copyButtonText || '📋 Copiar Código';
    const qrButtonText          = data?.qrButtonText || '📱Ver QR Code';
    const pendingTimeoutMinutes = data?.pendingTimeoutMinutes ?? 30;
    const showPlanBeforePix     = Boolean(data?.showPlanBeforePix);
    const planBeforePixMessage  = data?.planBeforePixMessage || '';
    const planBeforePixButtonText = data?.planBeforePixButtonText || '';
    const showPaymentInstructions = data?.showPaymentInstructions !== false;
    const paymentInstructionsText = data?.paymentInstructionsText || '';

    const change = (patch) => data?.onChangePayment?.(id, patch);

    const buttonValues = {
        showVerifyButton, showCopyButton, showQrButton,
        verifyButtonText, copyButtonText, qrButtonText,
    };

    return (
        <div className="mt-2 space-y-2 pl-2">
            {(!paymentPlanName || !parseFloat(paymentAmount)) && (
                <div className="flex items-center gap-1.5 rounded-md bg-red-500/10 border border-red-500/30 px-2 py-1.5 text-[10px] text-red-400">
                    <i className="fas fa-exclamation-triangle" />
                    <span>{!paymentPlanName ? 'Nome do plano obrigatório.' : 'Valor deve ser maior que zero.'}</span>
                </div>
            )}

            <div>
                <label className="text-[9px] text-white/40">Nome do Plano</label>
                <input type="text" className={`${INPUT_CLS} ${!paymentPlanName ? 'border-red-500/40' : 'border-white/[0.06]'}`}
                    placeholder="Ex: Acesso Premium" value={paymentPlanName}
                    onChange={(e) => change({ paymentPlanName: e.target.value })} />
            </div>

            <div>
                <label className="text-[9px] text-white/40">Valor (R$)</label>
                <input type="number" min="0" step="0.01"
                    className={`${INPUT_CLS} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${!parseFloat(paymentAmount) ? 'border-red-500/40' : 'border-white/[0.06]'}`}
                    placeholder="0.00" value={paymentAmount}
                    onChange={(e) => change({ paymentAmount: e.target.value })} />
            </div>

            <div>
                <label className="text-[9px] text-white/40">Duração do Acesso</label>
                <select className={SELECT_CLS} value={paymentPlanDuration}
                    onChange={(e) => change({ paymentPlanDuration: e.target.value })}>
                    <option value="mensal">Mensal (30 dias)</option>
                    <option value="semanal">Semanal</option>
                    <option value="quinzenal">Quinzenal</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="semestral">Semestral</option>
                    <option value="anual">Anual</option>
                    <option value="vitalicio">Vitalício</option>
                </select>
            </div>

            <div>
                <label className="text-[9px] text-white/40">Formato do Código PIX</label>
                <select className={SELECT_CLS} value={pixCodeFormat}
                    onChange={(e) => change({ pixCodeFormat: e.target.value })}>
                    {PIX_CODE_FORMATS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                </select>
            </div>

            {/* Mostrar Plano Antes do PIX */}
            <div className="border-t border-white/[0.06] pt-2 mt-2">
                <div className="flex items-center justify-between py-1">
                    <div>
                        <label className="text-[9px] text-white/40">Mostrar Plano Antes do PIX</label>
                        <p className="text-[8px] text-white/30 mt-0.5">Exibe detalhes do plano antes de gerar o código PIX</p>
                    </div>
                    <FlowPaySwitch checked={showPlanBeforePix} onChange={(v) => change({ showPlanBeforePix: v })} />
                </div>
                {showPlanBeforePix && (
                    <div className="mt-2 space-y-2 pl-1">
                        <div>
                            <label className="text-[9px] text-white/40">Mensagem Personalizada</label>
                            <AutoGrowTextarea className={TEXTAREA_CLS} placeholder="Ex: Você escolheu o plano..."
                                value={planBeforePixMessage}
                                onChange={(e) => change({ planBeforePixMessage: e.target.value })} />
                            <p className="text-[8px] text-white/30 mt-0.5">Variáveis disponíveis: {'{plano}, {duracao}, {valor}'}</p>
                            <p className="text-[8px] text-white/30 mt-0.5">Deixe vazio para usar padrão</p>
                        </div>
                        <div>
                            <label className="text-[9px] text-white/40">Texto do Botão</label>
                            <input type="text" className={`${INPUT_CLS} border-white/[0.06]`}
                                placeholder="Ex: 💠 Gerar PIX" value={planBeforePixButtonText}
                                onChange={(e) => change({ planBeforePixButtonText: e.target.value })} />
                            <p className="text-[8px] text-white/30 mt-0.5">Padrão: "💠 Gerar PIX"</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Mensagens Personalizadas */}
            <div className="border-t border-white/[0.06] pt-2 mt-2">
                <p className="text-[10px] text-[#22c55e] font-medium mb-2">Mensagens Personalizadas</p>
                <div>
                    <label className="text-[9px] text-white/40">Mensagem antes do código</label>
                    <AutoGrowTextarea className={TEXTAREA_CLS} placeholder="Ex: Copie o código abaixo:"
                        value={messageBefore}
                        onChange={(e) => change({ messageBefore: e.target.value })} />
                    <p className="text-[8px] text-white/30 mt-0.5">Padrão: "Copie o código abaixo:"</p>
                </div>
                <div className="mt-2">
                    <label className="text-[9px] text-white/40">Mensagem após o código</label>
                    <AutoGrowTextarea className={`${TEXTAREA_CLS} min-h-[40px]`} placeholder="Ex: Após efetuar o pagamento, clique no botão abaixo"
                        value={messageAfter}
                        onChange={(e) => change({ messageAfter: e.target.value })} />
                    <p className="text-[8px] text-white/30 mt-0.5">Padrão: "Após efetuar o pagamento, clique no botão abaixo ⬇️"</p>
                </div>
            </div>

            {/* Botões Personalizados */}
            <div className="border-t border-white/[0.06] pt-2 mt-2">
                <p className="text-[10px] text-[#22c55e] font-medium mb-2">Botões Personalizados</p>
                {PAYMENT_BUTTONS.map(({ key, checkedKey, textKey, placeholder }) => (
                    <div key={key} className="flex items-center gap-1.5 mt-1.5">
                        <input type="checkbox"
                            className="h-3 w-3 shrink-0 rounded border-white/20 bg-white/10 accent-emerald-500"
                            checked={buttonValues[checkedKey]}
                            onChange={(e) => change({ [checkedKey]: e.target.checked })} />
                        <input type="text"
                            className="flex flex-1 min-w-0 rounded-xl border border-white/[0.06] px-3 py-1.5 placeholder:text-white/20 transition-all hover:border-white/10 focus:outline-none focus:ring-1 focus:ring-[var(--brand-500)]/30 h-7 bg-[#111111] text-white text-xs disabled:opacity-40"
                            placeholder={placeholder}
                            value={buttonValues[textKey]}
                            disabled={!buttonValues[checkedKey]}
                            onChange={(e) => change({ [textKey]: e.target.value })} />
                    </div>
                ))}
            </div>

            {/* Instruções de Pagamento */}
            <div className="border-t border-white/[0.06] pt-2 mt-2">
                <div className="flex items-center justify-between py-1">
                    <div>
                        <label className="text-[9px] text-white/40">Instruções de Pagamento</label>
                        <p className="text-[8px] text-white/30 mt-0.5">Envia "Como realizar o pagamento..." antes do PIX</p>
                    </div>
                    <FlowPaySwitch checked={showPaymentInstructions} onChange={(v) => change({ showPaymentInstructions: v })} />
                </div>
                {showPaymentInstructions && (
                    <div className="mt-2">
                        <label className="text-[9px] text-white/40">Texto Personalizado</label>
                        <AutoGrowTextarea className={TEXTAREA_CLS}
                            placeholder={"✅ Como realizar o pagamento:\n\n1. Abra o aplicativo do seu banco.\n2. Selecione \"Pagar\" ou \"PIX\".\n3. Escolha \"PIX Copia e Cola\".\n4. Cole a chave abaixo e finalize."}
                            value={paymentInstructionsText}
                            onChange={(e) => change({ paymentInstructionsText: e.target.value })} />
                        <p className="text-[8px] text-white/30 mt-0.5">Deixe vazio para usar a mensagem padrão</p>
                    </div>
                )}
            </div>

            {/* Timeout */}
            <div className="border-t border-white/[0.06] pt-2 mt-2">
                <div className="flex items-center gap-1.5 mb-1.5">
                    <i className="fas fa-timer text-white/40 text-[10px]" />
                    <label className="text-[9px] text-white/40">Tempo para "Não Pago"</label>
                </div>
                <div className="flex items-center gap-1.5">
                    <input type="number" min="1" max="1440"
                        className="w-16 h-6 bg-[#111111] border border-white/[0.06] text-white text-[10px] rounded px-1.5 text-center focus:outline-none focus:ring-1 focus:ring-[var(--brand-500)]/30"
                        value={pendingTimeoutMinutes}
                        onChange={(e) => change({ pendingTimeoutMinutes: e.target.value })} />
                    <span className="text-[9px] text-white/40">minutos</span>
                </div>
                <p className="text-[8px] text-white/30 mt-1">Se não pagar, segue pelo caminho "NÃO PAGO" após esse tempo</p>
            </div>

            <div className="flex justify-between text-[9px] font-semibold px-1 pt-2 pb-1 border-t border-white/[0.06] mt-2">
                <span className="text-red-400">❌ Não Pago</span>
                <span className="text-emerald-400">✅ Pago</span>
            </div>
        </div>
    );
}
